import {
  cilCheckCircle,
  cilChevronRight,
  cilGamepad,
  cilOptions,
  cilPeople,
  cilPlus,
  cilSearch,
  cilSettings,
  cilUser,
} from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CDropdown,
  CDropdownDivider,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormInput,
  CInputGroup,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import CommonService from 'src/service/CommonService'
import ClubService from 'src/service/ClubService'
import GameStatusModal from './GameStatusModal'
import TransferAdminModal from './TransferAdminModal'
import ToastComponent from 'src/components/common/TaostComponent'
import { Link, useNavigate } from 'react-router-dom'
import PageInfoModal from 'src/components/PageInfoModal'
import AdminUserList from './AdminUserList'
import GameOnlineMembers from './GameOnlineMembers'
import appStoreBadge from '../../assets/images/store-badges/app-store-badge.svg'
import googlePlayBadge from '../../assets/images/store-badges/google-play-badge.png'
import './GameAccount.css'

const APP_STORE_URL = 'https://apps.apple.com/us/app/myclubtap/id6780349035'
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.myclubtap.app&pcampaignid=web_share'

const ALL = 'all'
const ACTIVE = 'active'
const COMPLETED = 'completed'

const TABS = [
  { key: ALL, label: 'All' },
  { key: ACTIVE, label: 'Active' },
  { key: COMPLETED, label: 'Completed' },
]

// user_games.game_type — the same four labels the old badge row showed.
const GAME_TYPES = {
  1: { label: 'Club', modifier: 'club' },
  2: { label: 'League', modifier: 'league' },
  3: { label: 'Indoor', modifier: 'indoor' },
  4: { label: 'Match', modifier: 'match' },
}

// Logos are optional and the uploads path 404s for games that never had one, so fall back
// to an initial tile instead of leaving a broken image in every row.
const GameLogo = ({ game }) => {
  const [failed, setFailed] = useState(false)
  const src = game.logo ? `${process.env.REACT_APP_API_URL}uploads/game_logo/${game.logo}` : null

  if (!src || failed) {
    return (
      <span className="ga-game-logo ga-game-logo--fallback" aria-hidden="true">
        {(game.game_name || '?').charAt(0).toUpperCase()}
      </span>
    )
  }

  return (
    <img className="ga-game-logo" src={src} alt="" onError={() => setFailed(true)} loading="lazy" />
  )
}

// Same logo the header avatar shows; falls back to the hub's initials when there is none.
const HubAvatar = ({ logo, initials }) => {
  const [failed, setFailed] = useState(false)

  if (!logo || failed) {
    return <span className="ga-hub-avatar">{initials || 'H'}</span>
  }

  return (
    <img
      className="ga-hub-avatar"
      src={`https://cricketapi.myclubtap.com/uploads/club/${logo}`}
      alt=""
      onError={() => setFailed(true)}
    />
  )
}

const StatCard = ({ icon, tone, label, value }) => (
  <CCard className="ga-stat-card h-100">
    <CCardBody className="d-flex align-items-center gap-3">
      <span className={`ga-stat-icon ga-stat-icon--${tone}`}>
        <CIcon icon={icon} size="lg" />
      </span>
      <div>
        <div className="ga-stat-label">{label}</div>
        <div className="ga-stat-value">{value ?? 0}</div>
      </div>
    </CCardBody>
  </CCard>
)

const GameAccount = () => {
  const [visible, setVisible] = useState(false)
  const [openActionsId, setOpenActionsId] = useState(null)
  const [activeTab, setActiveTab] = useState(ALL)
  const [activePage, setActivePage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()
  const [dashboardData, setDashboardData] = useState()
  const [loading, setLoading] = useState()
  const [gameStatus, setGameStatus] = useState()
  const [userId, setUserId] = useState()
  const [transferVisible, setTransferVisible] = useState(false)
  const [infoPageModal, setInfoPageModal] = useState(false)
  const [infoText] = useState('')
  const [hubLogo, setHubLogo] = useState(() => JSON.parse(localStorage.getItem('admin'))?.club_logo)
  // The game whose online members the modal is showing; null keeps the modal closed.
  const [onlineGame, setOnlineGame] = useState(null)

  const adminDetails = JSON.parse(localStorage.getItem('admin'))
  const runningActive = activeTab === ACTIVE
  const completedActive = activeTab === COMPLETED

  const hubName = adminDetails?.club_name || 'Your hub'
  const hubInitials = hubName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')

  const totalGames = dashboardData?.all_games ?? 0
  const completedGames = dashboardData?.complete_games ?? 0
  const activeGames = Math.max(totalGames - completedGames, 0)

  const columns = [
    { key: 'game_name', label: 'Game', sorter: true, _style: { width: '40%' } },
    { key: 'status', label: 'Status', filter: false, sorter: false },
    { key: 'online', label: 'Online', filter: false, sorter: false },
    { key: 'actions', label: 'Actions', filter: false, sorter: false },
  ]

  // localStorage only holds whatever the logo was at login, so ask the same endpoint the
  // header avatar uses for the current one.
  useEffect(() => {
    ClubService.getClubDetails()
      .then((res) => {
        if (res.status === 200) setHubLogo(res.user_details?.club_logo)
      })
      .catch(() => {})
  }, [])

  // One search box in place of CSmartTable's per-column filter row; the listing API only
  // filters on game_name anyway.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchText.trim()
      setColumnFilter((current) => {
        const next = term ? { game_name: term } : {}
        const unchanged =
          Object.keys(current).length === Object.keys(next).length &&
          Object.keys(next).every((key) => current[key] === next[key])
        return unchanged ? current : next
      })
    }, 350)
    return () => clearTimeout(timer)
  }, [searchText])

  useEffect(() => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    CommonService.gameAccount(
      offset,
      itemsPerPage,
      activePage,
      params,
      runningActive,
      completedActive,
    ).then((result) => {
      setUsers(result.data)
      setDashboardData(result.new_data)
      setLoading(false)
    })
  }, [activePage, columnFilter, columnSorter, itemsPerPage, runningActive, completedActive])

  const handleTabChange = (key) => {
    if (key === activeTab) return
    setActivePage(1)
    setActiveTab(key)
  }

  const showOnlineMembers = (game) => {
    setOnlineGame({ id: game.id, game_name: game.game_name })
  }

  const handleGameStatus = (status, userId) => {
    setVisible(true)
    setGameStatus(status)
    setUserId(userId)
  }

  const navigate = useNavigate()
  const gameLogin = (userId, gameId) => {
    const data = {}
    data.userId = userId
    data.gameId = gameId
    localStorage.setItem('gameId', gameId)
    CommonService.gameLogin(data).then((res) => {
      if (res.status === 200) {
        localStorage.setItem('admin', JSON.stringify(res.user))
        localStorage.setItem('token', JSON.stringify(res.token))
        navigate('/game-home')
      }
    })
  }
  const getStarted = (userId, gameId) => {
    const data = {}
    data.userId = userId
    data.gameId = gameId
    localStorage.setItem('gameId', gameId)
    CommonService.gameLogin(data).then((res) => {
      if (res.status === 200) {
        localStorage.setItem('admin', JSON.stringify(res.user))
        localStorage.setItem('token', JSON.stringify(res.token))
        navigate('/get-started')
      }
    })
  }

  const enterGameAdmin = (userId, gameId, url) => {
    const data = {}
    data.userId = userId
    data.gameId = gameId
    localStorage.setItem('gameId', gameId)
    CommonService.gameLogin(data)
      .then((res) => {
        if (res.status === 200) {
          localStorage.setItem('admin', JSON.stringify(res.user))
          localStorage.setItem('token', JSON.stringify(res.token))
          navigate(url)
        }
      })
      .catch((e) => {
        ToastComponent(e.response?.data?.message, 'error')
      })
  }

  const redirectToURL = () => {
    navigate('/season-register')
  }

  return (
    <>
      {adminDetails?.user_role_id !== 1 && (
        <>
          <div className="ge-page-header">
            <h1 className="ge-page-title">Admin Home</h1>
            <p className="ge-page-subtitle">Manage your organisation, hub and fantasy games.</p>
          </div>

          <CRow className="g-3 mb-4">
            <CCol xs={12} sm={6} xl={3}>
              <StatCard icon={cilGamepad} tone="violet" label="Active Games" value={activeGames} />
            </CCol>
            <CCol xs={12} sm={6} xl={3}>
              <StatCard
                icon={cilCheckCircle}
                tone="blue"
                label="Completed Games"
                value={completedGames}
              />
            </CCol>
            <CCol xs={12} sm={6} xl={3}>
              <StatCard
                icon={cilPeople}
                tone="green"
                label="Hub Members"
                value={dashboardData?.all_members}
              />
            </CCol>
            <CCol xs={12} sm={6} xl={3}>
              <StatCard
                icon={cilUser}
                tone="amber"
                label="Total Players"
                value={dashboardData?.all_players}
              />
            </CCol>
          </CRow>

          <CRow className="g-3">
            <CCol xs={12} xl={8}>
              <CCard className="ga-panel h-100">
                <CCardBody>
                  <div className="ga-panel-toolbar">
                    <h2 className="ga-panel-title">Your Games</h2>
                    <div className="ga-panel-actions">
                      <CInputGroup className="ga-search">
                        <span className="ga-search-icon">
                          <CIcon icon={cilSearch} />
                        </span>
                        <CFormInput
                          value={searchText}
                          onChange={(e) => {
                            setActivePage(1)
                            setSearchText(e.target.value)
                          }}
                          placeholder="Search games..."
                          aria-label="Search games"
                        />
                      </CInputGroup>
                      <CDropdown alignment="end">
                        <CDropdownToggle color="secondary" variant="outline" title="Tools">
                          <CIcon icon={cilSettings} className="me-1" />
                          Tools
                        </CDropdownToggle>
                        <CDropdownMenu>
                          {/* <CDropdownItem href="/duplicate-game">Duplicate Game</CDropdownItem> */}
                          <CDropdownItem href="/copy-players">Copy Players</CDropdownItem>
                          <CDropdownItem href="/copy-branding">Copy Branding</CDropdownItem>
                          <CDropdownItem href="/copy-sponsors">Copy Sponsor</CDropdownItem>
                        </CDropdownMenu>
                      </CDropdown>
                      <CButton color="primary" onClick={redirectToURL} title="Start New Game">
                        <CIcon icon={cilPlus} className="me-1" />
                        Create Game
                      </CButton>
                    </div>
                  </div>

                  <div className="ge-tabs ga-tabs" role="tablist">
                    {TABS.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={`ge-tab${activeTab === tab.key ? ' active' : ''}`}
                        onClick={() => handleTabChange(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="ge-datatable ge-datatable--no-select ge-datatable--static ga-table">
                    <CSmartTable
                      columns={columns}
                      columnSorter={{ external: true }}
                      noItemsLabel="No games found"
                      scopedColumns={{
                        game_name: (item) => {
                          const type = GAME_TYPES[item.game_type]
                          return (
                            <td>
                              <div className="ga-game-cell">
                                <GameLogo game={item} />
                                <div className="ga-game-meta">
                                  <Link
                                    className="ga-game-name"
                                    onClick={() => gameLogin(item.club_id, item.id)}
                                  >
                                    {item.game_name}
                                  </Link>
                                  {type && (
                                    <span className={`ga-pill ga-pill--${type.modifier}`}>
                                      {type.label}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                          )
                        },
                        status: (item) => (
                          <td>
                            <span
                              className={`ga-status ga-status--${
                                item.status === 1 ? 'progress' : 'done'
                              }`}
                            >
                              <span className="ga-status-dot" />
                              {item.status === 1 ? 'In Progress' : 'Completed'}
                            </span>
                          </td>
                        ),
                        online: (item) => (
                          <td>
                            <button
                              type="button"
                              className={`ga-online-count${
                                item.online_count > 0 ? ' ga-online-count--live' : ''
                              }`}
                              onClick={() => showOnlineMembers(item)}
                              title={`Show members online in ${item.game_name}`}
                            >
                              <CIcon icon={cilPeople} />
                              {item.online_count ?? 0}
                            </button>
                          </td>
                        ),
                        actions: (item) => (
                          <td
                            className={`dropdown-cell${
                              openActionsId === item.id ? ' dropdown-open' : ''
                            }`}
                          >
                            <CDropdown
                              alignment="end"
                              placement="bottom-end"
                              portal
                              onShow={() => setOpenActionsId(item.id)}
                              onHide={() =>
                                setOpenActionsId((current) =>
                                  current === item.id ? null : current,
                                )
                              }
                            >
                              <CDropdownToggle
                                color="secondary"
                                variant="ghost"
                                size="sm"
                                caret={false}
                                className="ge-row-actions-toggle"
                                aria-label="Game actions"
                              >
                                <CIcon icon={cilOptions} />
                              </CDropdownToggle>
                              <CDropdownMenu className="game-actions-menu">
                                <CDropdownItem
                                  onClick={() => handleGameStatus(item.status, item.id)}
                                >
                                  Game Status
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() =>
                                    enterGameAdmin(item.club_id, item.id, '/game-structure')
                                  }
                                >
                                  Game Structure
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() =>
                                    enterGameAdmin(item.club_id, item.id, '/manage-scores')
                                  }
                                >
                                  Manage Scores
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() => enterGameAdmin(item.club_id, item.id, '/members')}
                                >
                                  Members List
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() => enterGameAdmin(item.club_id, item.id, '/players')}
                                >
                                  Players List
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() =>
                                    enterGameAdmin(item.club_id, item.id, '/point-system')
                                  }
                                >
                                  Point System
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() => enterGameAdmin(item.club_id, item.id, '/rounds')}
                                >
                                  Rounds
                                </CDropdownItem>

                                {/* <CDropdownDivider /> */}
                              </CDropdownMenu>
                            </CDropdown>
                          </td>
                        ),
                      }}
                      items={users?.data}
                      itemsPerPage={itemsPerPage}
                      itemsPerPageSelect
                      loading={loading}
                      pagination={{ external: true }}
                      paginationProps={{
                        activePage: activePage,
                        pages: Math.ceil(users?.total / itemsPerPage) || 1,
                      }}
                      tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                      onActivePageChange={(activePage) => setActivePage(activePage)}
                      onItemsPerPageChange={(itemsPerPage) => {
                        setActivePage(1)
                        setItemsPerPage(itemsPerPage)
                      }}
                      onSorterChange={(sorter) => setColumnSorter(sorter)}
                    />
                  </div>
                </CCardBody>
              </CCard>
            </CCol>

            <CCol xs={12} xl={4}>
              <CCard className="ga-panel h-100">
                <CCardBody>
                  <h2 className="ga-panel-title mb-3">Hub overview</h2>

                  <div className="ga-hub-identity">
                    <HubAvatar logo={hubLogo} initials={hubInitials} />
                    <span className="ga-hub-name">{hubName}</span>
                  </div>

                  <div className="ga-hub-link">
                    <p className="ga-hub-badges-subtitle">
                      Members can discover and join your hub from the MyClubTap app.
                    </p>
                    <div className="ga-hub-badges">
                      <a
                        className="ga-hub-badge-link"
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={appStoreBadge} alt="Download on the App Store" />
                      </a>
                      <a
                        className="ga-hub-badge-link"
                        href={PLAY_STORE_URL}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={googlePlayBadge} alt="Get it on Google Play" />
                      </a>
                    </div>
                  </div>

                  <div className="ga-hub-stats">
                    <div className="ga-hub-stat">
                      <span>Hub Members</span>
                      <strong>{dashboardData?.all_members ?? 0}</strong>
                    </div>
                    <div className="ga-hub-stat">
                      <span>Total Players</span>
                      <strong>{dashboardData?.all_players ?? 0}</strong>
                    </div>
                    <div className="ga-hub-stat">
                      <span>Total Games</span>
                      <strong>{totalGames}</strong>
                    </div>
                    <div className="ga-hub-stat">
                      <span>Completed Games</span>
                      <strong>{completedGames}</strong>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="ga-hub-settings"
                    onClick={() => navigate('/welcome-message')}
                  >
                    Manage hub settings
                    <CIcon icon={cilChevronRight} />
                  </button>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </>
      )}

      {adminDetails?.user_role_id === 1 && (
        <>
          <div className="ge-page-header">
            <h1 className="ge-page-title">Users</h1>
            <p className="ge-page-subtitle">Manage the accounts registered on the platform.</p>
          </div>
          <CCard className="ga-panel">
            <CCardBody>
              <AdminUserList />
            </CCardBody>
          </CCard>
        </>
      )}

      {onlineGame && (
        <GameOnlineMembers
          gameId={onlineGame.id}
          gameName={onlineGame.game_name}
          onClose={() => setOnlineGame(null)}
        />
      )}
      <GameStatusModal
        visible={visible}
        setVisible={setVisible}
        gameStatus={gameStatus}
        setGameStatus={setGameStatus}
        userId={userId}
        setUsers={setUsers}
      />
      <TransferAdminModal
        transferVisible={transferVisible}
        setTransferVisible={setTransferVisible}
        userId={userId}
      />
      <PageInfoModal
        infoPageModal={infoPageModal}
        setInfoPageModal={setInfoPageModal}
        infoText={infoText}
      />
    </>
  )
}

export default GameAccount
